import React, { useState } from "react";
import {
  FaTimes,
  FaSpinner,
  FaExclamationTriangle,
  FaShoppingBag,
  FaInfoCircle,
  FaUndoAlt,
  FaCheck,
} from "react-icons/fa";

const REASONS = [
  "Ordered by mistake / Changed my mind",
  "Delivery time is too long",
  "Incorrect delivery address or contact number",
  "Want to change items or add more products",
  "Want to change payment method",
  "Other reason",
];

const CancelModal = ({ order, onConfirm, onClose, loading }) => {
  const [selectedReason, setSelectedReason] = useState(REASONS[0]);
  const [customRemarks, setCustomRemarks] = useState("");

  const orderNumber =
    order?.order_number || (order?.order_id || order?.id ? `#${order.order_id || order.id}` : null);
  const totalAmount = parseFloat(order?.total_amount ?? order?.total ?? 0);
  const items = order?.items || order?.order_items || [];
  const isOnlinePaid = ["PAID", "SUCCESS"].includes(
    String(order?.payment_status || "").toUpperCase()
  );

  const getFinalRemarks = () => {
    if (selectedReason === "Other reason") {
      return customRemarks.trim() || "Other reason";
    }
    return customRemarks.trim()
      ? `${selectedReason} - ${customRemarks.trim()}`
      : selectedReason;
  };

  const handleConfirm = () => {
    const finalReason = getFinalRemarks();
    if (!finalReason) return;
    onConfirm(finalReason);
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl transition-all border border-gray-100 animate-in zoom-in-95 duration-200 max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header */}
        <div className="bg-gradient-to-r from-red-50 via-rose-50 to-orange-50 px-5 sm:px-6 py-4 sm:py-5 border-b border-red-100/80 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-600 text-white shadow-md shadow-red-200">
              <FaExclamationTriangle className="h-5 w-5" />
            </div>
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-red-100/80 px-2.5 py-0.5 text-[11px] font-bold text-red-700 uppercase tracking-wider mb-0.5">
                Cancel Order
              </div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
                Are you sure you want to cancel?
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-full p-2 text-gray-400 hover:bg-white/80 hover:text-gray-700 transition"
            aria-label="Close modal"
          >
            <FaTimes className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto px-5 sm:px-6 py-4 sm:py-5 space-y-4 text-sm text-gray-700">
          {/* Order Summary Snapshot (if available) */}
          {orderNumber && (
            <div className="rounded-2xl border border-gray-200/80 bg-gray-50/80 p-3 sm:p-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white border border-gray-200 text-[#046a38]">
                  <FaShoppingBag className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">
                    Order Reference
                  </p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900 truncate">
                    {orderNumber}
                  </p>
                  {items.length > 0 && (
                    <p className="text-[11px] text-gray-500">
                      {items.length} {items.length === 1 ? "item" : "items"}
                    </p>
                  )}
                </div>
              </div>
              {totalAmount > 0 && (
                <div className="text-right shrink-0">
                  <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">Order Total</p>
                  <p className="text-sm sm:text-base font-extrabold text-[#046a38]">
                    ₹{totalAmount.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Refund Notice (if paid online) */}
          {isOnlinePaid ? (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 sm:p-3.5 flex items-start gap-2.5 text-xs text-emerald-900">
              <FaUndoAlt className="h-4 w-4 shrink-0 text-emerald-700 mt-0.5" />
              <div>
                <span className="font-bold">Refund Notice: </span>
                Your payment of ₹{totalAmount.toFixed(2)} will be initiated for refund back to your original payment method.
              </div>
            </div>
          ) : (
            <div className="rounded-2xl bg-amber-50/70 border border-amber-200/80 p-3 flex items-start gap-2 text-xs text-amber-900">
              <FaInfoCircle className="h-4 w-4 shrink-0 text-amber-700 mt-0.5" />
              <span>
                Payment method is <strong>Cash on Delivery (COD)</strong>. No charges were made.
              </span>
            </div>
          )}

          {/* Select Reason */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
              Reason for Cancellation:
            </label>
            <div className="space-y-2">
              {REASONS.map((reason) => {
                const isSelected = selectedReason === reason;
                return (
                  <label
                    key={reason}
                    onClick={() => setSelectedReason(reason)}
                    className={`flex items-center gap-3 rounded-xl border p-2.5 sm:p-3 cursor-pointer transition-all ${
                      isSelected
                        ? "border-red-500 bg-red-50/50 text-gray-900 font-semibold shadow-xs"
                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-all ${
                        isSelected
                          ? "border-red-600 bg-red-600 text-white"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {isSelected && <FaCheck className="h-2 w-2" />}
                    </div>
                    <span className="text-xs sm:text-sm select-none">{reason}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Additional Remarks */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Additional Details (Optional):
            </label>
            <textarea
              rows={2}
              value={customRemarks}
              onChange={(e) => setCustomRemarks(e.target.value)}
              className="w-full resize-none rounded-xl border border-gray-200 p-2.5 text-xs sm:text-sm outline-none transition focus:border-red-500 focus:ring-2 focus:ring-red-100"
              placeholder="Tell us any specific reason or feedback..."
              maxLength={250}
            />
            <div className="flex justify-between text-[11px] text-gray-400 mt-1">
              <span>Your feedback helps us improve our service.</span>
              <span>{customRemarks.length}/250</span>
            </div>
          </div>
        </div>

        {/* Bottom Actions Footer */}
        <div className="border-t border-gray-100 bg-gray-50/80 px-5 sm:px-6 py-3.5 sm:py-4 flex flex-col sm:flex-row items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-gray-300 bg-white text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-100 transition shadow-xs disabled:opacity-50"
          >
            Keep Order
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || !getFinalRemarks().trim()}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-6 py-2.5 text-xs sm:text-sm font-bold text-white shadow-md shadow-red-200 hover:bg-red-700 active:scale-95 transition disabled:opacity-60"
          >
            {loading ? (
              <>
                <FaSpinner className="h-3.5 w-3.5 animate-spin" />
                <span>Cancelling...</span>
              </>
            ) : (
              <span>Cancel Order</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelModal;